// src/pages/Analise/DilutionInput.jsx
// Controle de diluição (expoente 0..6 => 10^-n) reutilizável. Extraído do
// AnaliseFormModal para ser compartilhado com o AnaliseAutoCreateModal.
import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Collapse,
    IconButton,
    Slider,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as Lucide from 'lucide-react';

const MARKS = [
    { value: 0, label: '0' },
    { value: 1, label: '-1' },
    { value: 2, label: '-2' },
    { value: 3, label: '-3' },
    { value: 4, label: '-4' },
    { value: 5, label: '-5' },
    { value: 6, label: '-6' },
];

const DilutionInput = ({ value, onChange, readOnly }) => {
    const theme = useTheme();
    const [isEditing, setIsEditing] = useState(false);
    const [sliderVal, setSliderVal] = useState(Number(value) || 0);

    useEffect(() => {
        if (!isEditing) setSliderVal(Number(value) || 0);
    }, [value, isEditing]);

    const handleChangeCommitted = (_, newVal) => {
        onChange(newVal);
        setIsEditing(false);
    };

    const hasValue = Number(value) > 0;

    const sliderDisplay =
        sliderVal > 0 ? (
            <span>
                10<sup style={{ fontSize: '0.85em' }}>-{sliderVal}</sup>
            </span>
        ) : (
            'Pura (0)'
        );

    return (
        <Box sx={{ width: '100%' }}>
            <Button
                variant={hasValue ? 'contained' : 'outlined'}
                onClick={() => !readOnly && setIsEditing((prev) => !prev)}
                disabled={readOnly}
                startIcon={<Lucide.Droplet size={18} />}
                fullWidth
                sx={{
                    justifyContent: 'center',
                    height: 40,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: hasValue ? '1rem' : '0.9rem',
                    ...(hasValue
                        ? {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: 'primary.dark',
                              borderColor: 'transparent',
                              '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                                  borderColor: 'transparent',
                              },
                          }
                        : {
                              color: 'text.secondary',
                              borderColor: 'divider',
                              '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                              },
                          }),
                }}
            >
                {hasValue ? (
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                        Diluição: 10
                        <sup style={{ marginLeft: 2, fontSize: '0.85em' }}>
                            -{value}
                        </sup>
                    </Box>
                ) : (
                    'Pura (Sem diluição)'
                )}
            </Button>

            <Collapse in={isEditing} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        px: 2,
                        pt: 2,
                        pb: 2,
                        mt: 1,
                        bgcolor: 'background.default',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        boxShadow: theme.shadows[1],
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1.5}
                    >
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color="primary"
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            Ajustar:{' '}
                            <span style={{ marginLeft: 6, fontSize: '1.1em' }}>
                                {sliderDisplay}
                            </span>
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setIsEditing(false)}
                            sx={{ p: 0.5, mr: -1 }}
                        >
                            <Lucide.X size={18} />
                        </IconButton>
                    </Stack>
                    <Box sx={{ px: 1.5 }}>
                        <Slider
                            value={sliderVal}
                            onChange={(_, v) => setSliderVal(v)}
                            onChangeCommitted={handleChangeCommitted}
                            min={0}
                            max={6}
                            step={1}
                            marks={MARKS}
                            disabled={readOnly}
                            sx={{
                                '& .MuiSlider-markLabel': {
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default DilutionInput;
